// src/pages/Users/Inscription/AdherentsMiniTable.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaRedo,
  FaSpinner,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "./../../../components/Button/Button";
import { useUser } from "./../../Home/UserContext";
import { getAdherentsByUser } from "./../../../services/adherentServices";
import { getGroupName } from "./../../../services/groupServices";
import { getSettings } from "./../../../services/settingsServices";
import RenewalModal from "./../../PagesUtilisateurs/InscriptionAdherent/RenewalModal";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getStatusClass(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20";
    case "Licencié Hors Club":
      return "bg-lime-100 text-lime-800 ring-lime-200 dark:bg-lime-500/10 dark:text-lime-200 dark:ring-lime-500/20";
    case "Inscrit":
      return "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/20";
    case "Cours d'essai":
      return "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/20";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-200 text-slate-700 ring-slate-300 dark:bg-white/10 dark:text-slate-300 dark:ring-white/10";
    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white ring-slate-500 dark:bg-slate-600 dark:text-white dark:ring-slate-500";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-white/10 dark:text-slate-300 dark:ring-white/10";
  }
}

const seasonTagClass = (saison, current) =>
  saison && current && saison === current
    ? "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-white/10 dark:text-slate-300 dark:ring-white/10";

const defaultAvatar = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

const toAbsolute = (u) => {
  if (!u) return "";

  const s = String(u).trim();

  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  ) {
    return s;
  }

  if (!s.startsWith("/uploads")) return s;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const getAdherentGroups = (adherent) => {
  const groups =
    adherent?.groups ||
    adherent?.Groups ||
    adherent?.groupes ||
    adherent?.Groupes ||
    [];

  return Array.isArray(groups) ? groups : [];
};

const mergeGroups = (oldGroups = [], newGroups = []) => {
  const map = new Map();

  [...oldGroups, ...newGroups].forEach((group) => {
    if (group?.id) {
      map.set(Number(group.id), group);
    }
  });

  return Array.from(map.values());
};

const getGroupNamesFromGroups = (groups = []) => {
  return groups
    .map((group) => group?.nom)
    .filter(Boolean);
};

/**
 * Petit tableau récap des adhérents de l'utilisateur.
 *
 * Props:
 * - className?: string
 * - limit?: number
 */
export default function AdherentsMiniTable({ className = "", limit = 5 }) {
  const { user } = useUser();
  const userId = user?.userId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adherents, setAdherents] = useState([]);
  const [groupNames, setGroupNames] = useState({});
  const [activeSeason, setActiveSeason] = useState("");

  const [showRenew, setShowRenew] = useState(false);
  const [renewAdherent, setRenewAdherent] = useState(null);

  const fetchGroupNamesForList = useCallback(async (list = []) => {
    const names = {};

    await Promise.all(
      list.map(async (adherent) => {
        try {
          const result = await getGroupName(adherent.id);
          names[adherent.id] = Array.isArray(result) ? result : [];
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des groupes de l'adhérent:",
            adherent?.id,
            error
          );
          names[adherent.id] = [];
        }
      })
    );

    return names;
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const settings = await getSettings();
        const season = settings?.[0]?.saison_active || "";

        setActiveSeason(season);

        const data = await getAdherentsByUser(userId);
        const list = Array.isArray(data) ? data : [];

        setAdherents(list);

        const names = await fetchGroupNamesForList(list);
        setGroupNames(names);
      } catch (error) {
        console.error(error);

        if (!silent) {
          toast.error("Erreur lors du chargement de vos adhérents.");
        }
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [fetchGroupNamesForList, userId]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rows = useMemo(() => adherents.slice(0, limit), [adherents, limit]);

  const openRenewalModal = (adherent) => {
    setRenewAdherent(adherent);
    setShowRenew(true);
  };

  const closeRenewalModal = async (saved = false, payload = null) => {
    setShowRenew(false);
    setRenewAdherent(null);

    if (!saved || !payload) return;

    const updatedAdherentId = Number(payload.adherentId);
    const selectedGroups = payload.selectedGroups || [];
    const allGroupsFromPayload = payload.allGroups || [];
    const nextSeason = payload.nextSeason || activeSeason;

    setAdherents((prev) =>
      prev.map((adherent) => {
        if (Number(adherent.id) !== updatedAdherentId) {
          return adherent;
        }

        const oldGroups = getAdherentGroups(adherent);

        const mergedGroups = allGroupsFromPayload.length
          ? allGroupsFromPayload
          : mergeGroups(oldGroups, selectedGroups);

        return {
          ...adherent,
          saison_active: nextSeason,
          statut_inscription: payload.statut_inscription || "Inscrit",

          groups: mergedGroups,
          Groups: mergedGroups,
          groupes: mergedGroups,
          Groupes: mergedGroups,
        };
      })
    );

    const updatedGroups = allGroupsFromPayload.length
      ? allGroupsFromPayload
      : selectedGroups;

    if (updatedGroups.length) {
      setGroupNames((prev) => {
        const previousNames = prev[updatedAdherentId] || [];
        const nextNames = getGroupNamesFromGroups(updatedGroups);

        return {
          ...prev,
          [updatedAdherentId]: Array.from(
            new Set([...previousNames, ...nextNames])
          ),
        };
      });
    }

    try {
      await loadData({ silent: true });
    } catch {
      // loadData gère déjà l'erreur
    }
  };

  if (!userId) return null;

  if (loading) {
    return (
      <div
        className={cn(
          "mb-5 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950",
          className
        )}
      >
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-5 py-4">
          <div className="h-5 w-2/3 animate-pulse rounded-full bg-white/25" />
          <div className="mt-2 h-4 w-1/2 animate-pulse rounded-full bg-white/15" />
        </div>

        <div className="space-y-3 p-5">
          <div className="h-14 w-full animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-900" />
          <div className="h-14 w-full animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-900" />
        </div>
      </div>
    );
  }

  if (adherents.length === 0) return null;

  return (
    <div
      className={cn(
        "mb-5 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950",
        className
      )}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-700">
        <div className="absolute -right-12 -top-14 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-14 -left-12 h-36 w-36 rounded-full bg-cyan-300/20 blur-3xl" />

        <div className="relative flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20">
              <FaUserFriends />
            </div>

            <div>
              <h3 className="text-lg font-bold text-white">
                Vos adhérents déjà enregistrés
              </h3>

              <p className="mt-1 max-w-2xl text-sm text-white/80">
                Vous gérez déjà{" "}
                <span className="font-bold text-white">
                  {adherents.length} adhérent{adherents.length > 1 ? "s" : ""}
                </span>
                . Avant de créer une nouvelle fiche, vérifiez s’il s’agit d’un
                renouvellement.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {activeSeason && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white ring-1 ring-white/20">
                <FaCalendarAlt />
                Saison active : {activeSeason}
              </span>
            )}

            {refreshing && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white ring-1 ring-white/20">
                <FaSpinner className="animate-spin" />
                Synchronisation
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="hidden md:block px-4 py-4 sm:px-5">
        <div className="overflow-x-auto rounded-[1.5rem] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
          <table className="min-w-[820px] w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <th className="px-4 py-3">Adhérent</th>
                <th className="px-4 py-3">Saison</th>
                <th className="px-4 py-3">Groupes</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {rows.map((adherent) => {
                const canRenew =
                  activeSeason && adherent.saison_active !== activeSeason;

                return (
                  <tr
                    key={adherent.id}
                    className="bg-white transition-colors hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={
                            toAbsolute(adherent.photo_url) ||
                            defaultAvatar(adherent.genre)
                          }
                          alt={`${adherent.nom || ""} ${
                            adherent.prenom || ""
                          }`}
                          className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-white/10"
                        />

                        <div className="min-w-0">
                          <div className="truncate font-bold text-slate-900 dark:text-white">
                            {(adherent.nom || "").toUpperCase()}{" "}
                            {adherent.prenom || ""}
                          </div>

                          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            Dossier #{adherent.id}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1",
                          seasonTagClass(adherent.saison_active, activeSeason)
                        )}
                        title={`Saison actuelle : ${activeSeason || "—"}`}
                      >
                        {adherent.saison_active || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                      {groupNames[adherent.id]?.length ? (
                        <div className="flex max-w-[280px] flex-wrap gap-1.5">
                          {groupNames[adherent.id].map((name) => (
                            <span
                              key={`${adherent.id}-${name}`}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200"
                            >
                              <FaUsers className="text-[10px] text-blue-500" />
                              {name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1",
                          getStatusClass(adherent.statut_inscription)
                        )}
                      >
                        {adherent.statut_inscription || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-2">
                        <Button
                          text="Dossier"
                          icon={FaExternalLinkAlt}
                          onClick={() => navigate("/dossier-d-inscription")}
                          className="!rounded-xl !px-3 !py-1.5 !text-xs gap-2"
                        />

                        {canRenew ? (
                          <Button
                            text="Renouveler"
                            icon={FaRedo}
                            onClick={() => openRenewalModal(adherent)}
                            className="!rounded-xl !px-3 !py-1.5 !text-xs gap-2"
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20">
                            <FaCheckCircle />
                            À jour
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {adherents.length > limit && (
          <div className="mt-3 flex justify-end">
            <Button
              text={`Voir tous les dossiers (${adherents.length})`}
              onClick={() => navigate("/dossier-d-inscription")}
              className="!rounded-xl !px-3 !py-1.5 !text-xs"
            />
          </div>
        )}
      </div>

      <div className="space-y-3 px-4 py-4 md:hidden">
        {rows.map((adherent) => {
          const canRenew =
            activeSeason && adherent.saison_active !== activeSeason;

          return (
            <div
              key={adherent.id}
              className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950"
            >
              <div className="flex items-start gap-3 p-3">
                <img
                  src={
                    toAbsolute(adherent.photo_url) ||
                    defaultAvatar(adherent.genre)
                  }
                  alt={`${adherent.nom || ""} ${adherent.prenom || ""}`}
                  className="h-12 w-12 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-white/10"
                />

                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-slate-900 dark:text-white">
                    {(adherent.nom || "").toUpperCase()}{" "}
                    {adherent.prenom || ""}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ring-1",
                        seasonTagClass(adherent.saison_active, activeSeason)
                      )}
                    >
                      {adherent.saison_active || "—"}
                    </span>

                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ring-1",
                        getStatusClass(adherent.statut_inscription)
                      )}
                    >
                      {adherent.statut_inscription || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 px-3 py-3 dark:border-white/10">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Groupes
                </p>

                {groupNames[adherent.id]?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {groupNames[adherent.id].map((name) => (
                      <span
                        key={`${adherent.id}-${name}`}
                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200"
                      >
                        <FaUsers className="text-[10px] text-blue-500" />
                        {name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-slate-400">—</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 border-t border-slate-100 px-3 py-3 dark:border-white/10">
                <Button
                  text="Voir le dossier"
                  icon={FaExternalLinkAlt}
                  onClick={() => navigate("/dossier-d-inscription")}
                  className="!rounded-xl !px-3 !py-1.5 !text-xs"
                />

                {canRenew ? (
                  <Button
                    text="Renouveler"
                    icon={FaRedo}
                    onClick={() => openRenewalModal(adherent)}
                    className="!rounded-xl !px-3 !py-1.5 !text-xs"
                  />
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20">
                    <FaCheckCircle />
                    À jour
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {adherents.length > limit && (
          <div className="pt-1">
            <Button
              text={`Voir tous les dossiers (${adherents.length})`}
              onClick={() => navigate("/dossier-d-inscription")}
              className="!rounded-xl !px-3 !py-1.5 !text-xs"
            />
          </div>
        )}
      </div>

      {showRenew && (
        <RenewalModal
          isOpen={showRenew}
          adherent={renewAdherent}
          nextSeason={activeSeason}
          onClose={closeRenewalModal}
        />
      )}
    </div>
  );
}